import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import { Table, Card, Form, Row, Col, Button, InputGroup, FormControl, Badge } from 'react-bootstrap';
import mediaClientService from "../services/mediaClient.service";
import { FaChevronDown, FaChevronUp, FaSearch, FaSort, FaSortUp, FaSortDown, FaMobileAlt, FaUser, FaRegClock } from 'react-icons/fa';

const PhotobaseDevices = () => {
    const { user: currentUser } = useSelector((state) => state.auth);
    
    // State variables
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalDevicesCount, setTotalDevicesCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchName, setSearchName] = useState("");
    const [activeSearch, setActiveSearch] = useState("");
    const [page, setPage] = useState(0);
    const [orderBy, setOrderBy] = useState("clientName");
    const [sortDirection, setSortDirection] = useState("ASC");
    
    // Tracks which client rows are expanded
    const [expandedRows, setExpandedRows] = useState({});

    // Device list sorting states (frontend sorting)
    const [deviceSortField, setDeviceSortField] = useState("deviceDesc");
    const [deviceSortDirection, setDeviceSortDirection] = useState("ASC");

    // Tracks if a search or show-all has been initiated (prevents auto-loading on page mount)
    const [searchInitiated, setSearchInitiated] = useState(false);
    
    const pageSize = 50;

    // Load data when page, search, orderBy, or sortDirection changes
    useEffect(() => {
        if (searchInitiated && currentUser && currentUser.roles.includes("ROLE_ADMIN")) {
            fetchData(page, activeSearch, orderBy, sortDirection);
        }
    }, [page, activeSearch, orderBy, sortDirection, searchInitiated, currentUser]);

    const fetchData = (targetPage, search, order, direction) => {
        setLoading(true);
        mediaClientService.getPhotobaseUsage(targetPage, pageSize, search, order, direction).then(
            (response) => {
                const { content, totalElements } = response.data;
                const totalDevices = response.headers["x-total-devices"] || 0;
                setData(content || []);
                setTotalCount(totalElements || 0);
                setTotalDevicesCount(Number(totalDevices));
                setLoading(false);
            },
            (error) => {
                const errorMessage = (error.response && error.response.data) || error.message || error.toString();
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch photobase usage: " + errorMessage });
                setLoading(false);
            }
        );
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setPage(0);
        setActiveSearch(searchName);
        setSearchInitiated(true);
    };

    const handleClearFilter = () => {
        setSearchName("");
        setActiveSearch("");
        setPage(0);
        setSearchInitiated(false);
        setTotalDevicesCount(0);
    };

    const handleShowAll = () => {
        setSearchName("");
        setActiveSearch("");
        setPage(0);
        setSearchInitiated(true);
        if (searchInitiated) {
            fetchData(0, "", orderBy, sortDirection);
        }
    };

    const toggleRow = (clientId) => {
        setExpandedRows(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setOrderBy(field);
            setSortDirection("ASC");
        }
        setPage(0);
    };

    const handleDeviceSort = (field) => {
        if (deviceSortField === field) {
            setDeviceSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setDeviceSortField(field);
            setDeviceSortDirection("ASC");
        }
    };

    const renderDeviceSortIcon = (field) => {
        if (deviceSortField !== field) {
            return <FaSort className="ms-1 text-white-50" style={{ opacity: 0.6 }} />;
        }
        return deviceSortDirection === "ASC" 
            ? <FaSortUp className="ms-1 text-warning" />
            : <FaSortDown className="ms-1 text-warning" />;
    };

    const getSortedDevices = (devices) => {
        if (!devices) return [];
        return [...devices].sort((a, b) => {
            let valA = a[deviceSortField];
            let valB = b[deviceSortField];
            
            if (valA === undefined || valA === null) valA = "";
            if (valB === undefined || valB === null) valB = "";
            
            if (deviceSortField === "lastLogin") {
                const timeA = valA ? new Date(valA).getTime() : 0;
                const timeB = valB ? new Date(valB).getTime() : 0;
                return deviceSortDirection === "ASC" ? timeA - timeB : timeB - timeA;
            }
            
            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            
            if (strA < strB) return deviceSortDirection === "ASC" ? -1 : 1;
            if (strA > strB) return deviceSortDirection === "ASC" ? 1 : -1;
            return 0;
        });
    };

    const handleNextPage = () => {
        const totalPages = Math.ceil(totalCount / pageSize);
        if (page + 1 < totalPages) {
            setPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(prev => prev - 1);
        }
    };

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
                <div className="alert alert-danger">
                    <h4>Unauthorized Access</h4>
                    <p>You must be an administrator to view this page.</p>
                </div>
            </div>
        );
    }

    const totalPages = Math.ceil(totalCount / pageSize);

    // Helper to render sort icon
    const renderSortIcon = (field) => {
        if (orderBy !== field) {
            return <FaSort className="ms-1 text-muted" />;
        }
        return sortDirection === "ASC" 
            ? <FaSortUp className="ms-1 text-primary" />
            : <FaSortDown className="ms-1 text-primary" />;
    };

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <style>{`
                .sortable-device-header {
                    transition: color 0.15s ease-in-out;
                }
                .sortable-device-header:hover {
                    color: #ffc107 !important; /* bootstrap warning yellow */
                }
            `}</style>
            <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-3" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <div className="d-flex align-items-center">
                        <FaMobileAlt size={22} className="me-2 text-warning" />
                        <h4 className="mb-0 fw-semibold">Photobase Device Usage</h4>
                    </div>
                    <div className="d-flex align-items-center">
                        <Badge bg="secondary" className="px-3 py-2 fs-6 me-2">
                            Clients: {totalCount.toLocaleString()}
                        </Badge>
                        <Badge bg="info" className="px-3 py-2 fs-6 text-white">
                            Devices: {totalDevicesCount.toLocaleString()}
                        </Badge>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    
                    {/* Filter and Control Panel */}
                    <Form onSubmit={handleSearch} className="mb-4">
                        <Row className="g-3 align-items-end">
                            <Col lg={5} md={8}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-muted mb-2">Search Client Name</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <FaSearch className="text-muted" />
                                        </InputGroup.Text>
                                        <FormControl 
                                            placeholder="Enter client name..."
                                            value={searchName}
                                            onChange={(e) => setSearchName(e.target.value)}
                                            disabled={loading}
                                            className="border-start-0 ps-0"
                                        />
                                        {searchName && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={handleClearFilter} 
                                                disabled={loading}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                        <Button 
                                            type="submit" 
                                            variant="primary" 
                                            disabled={loading}
                                            className="fw-semibold"
                                        >
                                            Search
                                        </Button>
                                        <Button 
                                            variant="success" 
                                            onClick={handleShowAll}
                                            disabled={loading}
                                            className="text-white fw-semibold"
                                        >
                                            Show All Clients
                                        </Button>
                                    </InputGroup>
                                </Form.Group>
                            </Col>

                            <Col lg={3} md={4}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-muted mb-2">Order By</Form.Label>
                                    <Form.Select 
                                        value={`${orderBy}_${sortDirection}`} 
                                        onChange={(e) => {
                                            const [field, direction] = e.target.value.split("_");
                                            setOrderBy(field);
                                            setSortDirection(direction);
                                            setPage(0);
                                        }}
                                        disabled={loading}
                                        className="form-select"
                                    >
                                        <option value="clientName_ASC">Client Name (A to Z)</option>
                                        <option value="clientName_DESC">Client Name (Z to A)</option>
                                        <option value="deviceCount_ASC">Device Count (Lowest first)</option>
                                        <option value="deviceCount_DESC">Device Count (Highest first)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="d-flex justify-content-lg-end justify-content-between align-items-center">
                                <div className="d-flex align-items-center mt-3 mt-lg-0">
                                    <span className="me-3 text-muted fs-6">
                                        Page <strong>{page + 1}</strong> of {Math.max(1, totalPages)}
                                    </span>
                                    <Button 
                                        variant="outline-primary" 
                                        size="md" 
                                        className="me-2 px-3 fw-semibold" 
                                        onClick={handlePrevPage} 
                                        disabled={page === 0 || loading || !searchInitiated}
                                    >
                                        Previous
                                    </Button>
                                    <Button 
                                        variant="outline-primary" 
                                        size="md" 
                                        className="px-3 fw-semibold" 
                                        onClick={handleNextPage} 
                                        disabled={page + 1 >= totalPages || loading || !searchInitiated}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>

                    {/* Master Table */}
                    <div className="table-responsive border rounded-3 overflow-hidden">
                        <Table hover className="mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th 
                                        onClick={() => handleSort("clientName")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-4 fw-semibold text-dark"
                                    >
                                        Client Name / ID {renderSortIcon("clientName")}
                                    </th>
                                    <th 
                                        onClick={() => handleSort("deviceCount")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-3 fw-semibold text-dark text-center"
                                    >
                                        Registered Devices {renderSortIcon("deviceCount")}
                                    </th>
                                    <th className="py-3 px-4 fw-semibold text-dark text-end">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!searchInitiated ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted fw-semibold">
                                            <FaMobileAlt size={42} className="mb-3 text-secondary d-block mx-auto" />
                                            <h5 className="text-dark fw-bold">Ready to Load Photobase Devices</h5>
                                            <p className="mb-0 text-muted fs-6">Enter a client name pattern to search, or click the <strong>Show All Clients</strong> button to begin.</p>
                                        </td>
                                    </tr>
                                ) : loading ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5">
                                            <div className="spinner-border text-primary me-2" role="status"></div>
                                            <span className="text-muted fw-semibold fs-5">Loading Photobase records...</span>
                                        </td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted fw-semibold">
                                            No clients found matching the search criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((client) => {
                                        const isExpanded = !!expandedRows[client.clientId];
                                        return (
                                            <React.Fragment key={client.clientId}>
                                                <tr 
                                                    onClick={() => toggleRow(client.clientId)} 
                                                    style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                                                    className={isExpanded ? "table-active" : ""}
                                                >
                                                    <td className="text-center">
                                                        {isExpanded 
                                                            ? <FaChevronUp className="text-primary" /> 
                                                            : <FaChevronDown className="text-muted" />
                                                        }
                                                    </td>
                                                    <td className="px-4 fw-semibold text-dark fs-5">
                                                        <div>{client.clientName}</div>
                                                        <small className="text-muted monospace fw-normal" style={{ fontSize: '0.78rem', marginTop: '2px', display: 'block' }}>
                                                            ID: {client.clientId}
                                                        </small>
                                                    </td>
                                                    <td className="px-3 text-center">
                                                        <Badge bg={client.deviceCount > 0 ? "success" : "light"} className={client.deviceCount > 0 ? "px-3 py-2 fs-6 text-white" : "px-3 py-2 fs-6 text-dark border"}>
                                                            {client.deviceCount}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 text-end">
                                                        <Button 
                                                            variant={isExpanded ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fw-semibold px-3"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRow(client.clientId);
                                                            }}
                                                        >
                                                            {isExpanded ? "Hide" : "Show Devices"}
                                                        </Button>
                                                    </td>
                                                </tr>

                                                {/* Expanded Details Row */}
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="4" className="bg-light p-4 border-top">
                                                            <div className="ps-4 pe-2 py-2">
                                                                <h5 className="mb-3 text-dark fw-semibold d-flex align-items-center">
                                                                    <FaMobileAlt size={16} className="me-2 text-primary" />
                                                                    Registered Device List
                                                                </h5>
                                                                
                                                                {client.devices.length === 0 ? (
                                                                    <div className="alert alert-warning border-0 mb-0 py-3 rounded-3 shadow-xs">
                                                                        No registered Photobase devices found for this client.
                                                                    </div>
                                                                ) : (
                                                                    <div className="table-responsive border rounded-3 overflow-hidden shadow-sm bg-white">
                                                                        <Table hover size="sm" className="mb-0 align-middle">
                                                                            <thead className="table-dark">
                                                                                <tr>
                                                                                    <th 
                                                                                        onClick={() => handleDeviceSort("deviceDesc")} 
                                                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                        className="py-2 px-3 fw-medium text-white sortable-device-header"
                                                                                    >
                                                                                        Device Description {renderDeviceSortIcon("deviceDesc")}
                                                                                    </th>
                                                                                    <th className="py-2 px-3 fw-medium">Device ID</th>
                                                                                    <th 
                                                                                        onClick={() => handleDeviceSort("userName")} 
                                                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                        className="py-2 px-3 fw-medium text-white sortable-device-header"
                                                                                    >
                                                                                        Portal User {renderDeviceSortIcon("userName")}
                                                                                    </th>
                                                                                    <th className="py-2 px-3 fw-medium text-center">Status</th>
                                                                                    <th 
                                                                                        onClick={() => handleDeviceSort("lastLogin")} 
                                                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                        className="py-2 px-3 fw-medium text-white sortable-device-header"
                                                                                    >
                                                                                        <FaRegClock className="me-1" /> Last Login / Activity {renderDeviceSortIcon("lastLogin")}
                                                                                    </th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {getSortedDevices(client.devices).map((device) => (
                                                                                    <tr key={device.deviceId}>
                                                                                        <td className="px-3 py-3 fw-semibold text-dark">{device.deviceDesc || "No Description"}</td>
                                                                                        <td className="px-3 text-muted monospace fs-6">{device.deviceId}</td>
                                                                                        <td className="px-3">
                                                                                            <span className="d-flex align-items-center">
                                                                                                <FaUser className="text-secondary me-2" size={12} />
                                                                                                {device.userName}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-3 text-center">
                                                                                            <Badge bg={device.status === 1 ? "success" : "danger"} className="px-2 py-1.5 fs-7 text-white">
                                                                                                {device.status === 1 ? "Active" : "Disabled"}
                                                                                            </Badge>
                                                                                        </td>
                                                                                        <td className="px-3 text-muted fs-6">
                                                                                            {device.lastLogin 
                                                                                                ? new Date(device.lastLogin).toLocaleString() 
                                                                                                : <span className="text-warning">Never validated</span>
                                                                                            }
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination display footer */}
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-4">
                        <span className="text-muted mb-3 mb-sm-0">
                            Showing <strong>{data.length}</strong> of <strong>{totalCount}</strong> clients
                        </span>
                        {totalPages > 1 && (
                            <div className="d-flex align-items-center">
                                <Button 
                                    variant="outline-secondary" 
                                    className="me-2 fw-semibold"
                                    onClick={() => setPage(0)}
                                    disabled={page === 0 || loading}
                                >
                                    First
                                </Button>
                                <Button 
                                    variant="outline-secondary" 
                                    className="me-2 fw-semibold"
                                    onClick={handlePrevPage} 
                                    disabled={page === 0 || loading}
                                >
                                    Prev
                                </Button>
                                <span className="mx-2 font-weight-bold">
                                    Page <strong>{page + 1}</strong> of {totalPages}
                                </span>
                                <Button 
                                    variant="outline-secondary" 
                                    className="ms-2 me-2 fw-semibold"
                                    onClick={handleNextPage} 
                                    disabled={page + 1 >= totalPages || loading}
                                >
                                    Next
                                </Button>
                                <Button 
                                    variant="outline-secondary" 
                                    className="fw-semibold"
                                    onClick={() => setPage(totalPages - 1)}
                                    disabled={page + 1 >= totalPages || loading}
                                >
                                    Last
                                </Button>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default PhotobaseDevices;
